import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { getRFQs, deleteRFQ } from '../../../models/rfqModel';
import { toast } from 'react-toastify';
import { safeParseDate } from '../../../utils/dateUtils';
import {
  useReactTable,
  getCoreRowModel,
} from '@tanstack/react-table';

// Debounced Input for Global Filter
function DebouncedInput({
  value: initialValue,
  onChange,
  debounce = 500,
  ...props
}) {
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value, debounce]); // Removed onChange from dependencies to prevent infinite loop

  return (
    <input {...props} value={value} onChange={e => setValue(e.target.value)} />
  );
}

function RFQsList() {
  const [rfqs, setRFQs] = useState([]);
  const [loading, setLoading] = useState(true);
  const { can, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isLoadingRef = useRef(false); // Use ref to track loading state

  const initialPagination = () => {
    const searchParams = new URLSearchParams(location.search);
    const page = parseInt(searchParams.get('page')) || 1;
    const pageSize = parseInt(searchParams.get('pageSize')) || 20;
    return { pageIndex: page - 1, pageSize };
  };

  const [pagination, setPagination] = useState(initialPagination);
  const [rowCount, setRowCount] = useState(0);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState([]);
  
  // Initialize status filter from URL parameter
  const initialStatusFilter = () => {
    const searchParams = new URLSearchParams(location.search);
    return searchParams.get('status') || '';
  };
  
  const [statusFilter, setStatusFilter] = useState(initialStatusFilter); // All/Not Sent/Open/Closed/Ready to Send
  
  // Load view mode from localStorage, default to 'grid'
  const [viewMode, setViewMode] = useState(() => {
    const savedViewMode = localStorage.getItem('rfqsViewMode');
    return savedViewMode || 'grid';
  });

  // Save view mode to localStorage whenever it changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('rfqsViewMode', mode);
  };

  // Sync status filter with URL parameter when location changes
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlStatus = searchParams.get('status') || '';
    if (urlStatus !== statusFilter) {
      setStatusFilter(urlStatus);
      setPagination(p => ({ ...p, pageIndex: 0 })); // Reset to first page when filter changes
    }
  }, [location.search]); // Only depend on location.search to avoid infinite loops

 
  const fetchRFQs = useCallback(async () => {
   
    // Prevent multiple simultaneous calls using ref
    if (isLoadingRef.current) { 
      return;
    }
    
    isLoadingRef.current = true;
    setLoading(true);
    try {
      const params = {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        type: 'regular' // Always filter for regular RFQs only
      };
      
      if (globalFilter) {
        params.filter = globalFilter;
      }
      if (statusFilter) {
        params.status = statusFilter;
      }
      if (sorting.length > 0) {
        params.sortBy = sorting[0].id;
        params.sortOrder = sorting[0].desc ? 'DESC' : 'ASC';
      }
       
      
      // Add timeout to prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
      
      const response = await getRFQs(params);
      clearTimeout(timeoutId);
 
      if (response.status === 1 && response.data && response.data.data && response.data.meta) {
        // Backend now handles department filtering, so we just use the data as-is 
        setRFQs(response.data.data);
        setRowCount(response.data.meta.totalItems);
      } else if (response.status === 0) {
        // Handle permission denied or other errors
        toast.warn(response.message || 'Access denied');
        setRFQs([]);
        setRowCount(0);
      } else {
        toast.error(response.message || 'Failed to load RFQs');
        setRFQs([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error('Error fetching RFQs:', error);
      toast.error(error.message || 'An error occurred while loading RFQs');
      setRFQs([]);
      setRowCount(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [pagination, globalFilter, sorting, statusFilter]);

  const handleDelete = useCallback((id, title) => {
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      deleteRFQ(id)
        .then((response) => {
          if (response.status === 1) {
            toast.success('RFQ deleted successfully');
            fetchRFQs();
          } else {
            toast.error(response.message || 'Failed to delete RFQ');
          }
        })
        .catch((error) => {
          toast.error(error.message || 'An error occurred while deleting the RFQ');
        });
    }
  }, [fetchRFQs]);

  // Update URL when pagination changes, but avoid navigation loops
  useEffect(() => {
    const currentSearchParams = new URLSearchParams(location.search);
    const currentPage = parseInt(currentSearchParams.get('page')) || 1;
    const currentPageSize = parseInt(currentSearchParams.get('pageSize')) || 20;
    
    const newPage = pagination.pageIndex + 1;
    const newPageSize = pagination.pageSize;
    
    // Only update URL if pagination actually changed
    if (currentPage !== newPage || currentPageSize !== newPageSize) {
      currentSearchParams.set('page', newPage.toString());
      currentSearchParams.set('pageSize', newPageSize.toString());
      
      const newSearch = currentSearchParams.toString();
      const newUrl = `${location.pathname}?${newSearch}`;
      
      navigate(newUrl, { replace: true });
    }
  }, [pagination.pageIndex, pagination.pageSize, navigate, location.pathname]);

  // Fetch data when dependencies change
  useEffect(() => {
    fetchRFQs();
  }, [fetchRFQs]);

  const handleRefresh = useCallback(() => {
    fetchRFQs();
  }, [fetchRFQs]);

  const table = useReactTable({
    data: rfqs,
    columns: [
      // Simple column structure for compatibility
      { accessorKey: 'procurement_id', header: 'ID' }
    ],
    state: {
      pagination,
      globalFilter,
      sorting,
    },
    onPaginationChange: setPagination,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    pageCount: Math.ceil(rowCount / pagination.pageSize),
    debugTable: false, 
  });

  if (loading && rfqs.length === 0) {
    return (
      <div className="p-4 py-6 md:p-8">
        <div className="bg-white rounded-xl p-12 shadow-lg border border-gray-100">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
          </div>
        </div>
      </div>
    );
  }

  const rfqType = 'Regular'; // Only showing regular RFQs

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header Section */}
      <div className="container mx-auto px-4 py-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-4">
          <div className="flex justify-between items-start">
            {/* Left: Logo, Title and Results */}
            <div className="flex items-start">
              <div className="flex-shrink-0 rounded-lg p-2.5 mr-3 mt-0.5" style={{ backgroundColor: 'rgba(34, 197, 94, 0.15)' }}>
                <svg className="h-5 w-5" fill="none" stroke="#22C55E" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {rfqType} RFQs
                </h1>
                <p className="text-xs text-gray-500 mb-1">
                  Request for Quotations
                </p>
                <div className="flex items-center mt-1">
                  <span className="text-xs font-medium" style={{ color: '#22C55E' }}>
                    {rowCount ? `Showing ${Math.min(pagination.pageSize, rowCount)} of ${rowCount} results` : '0 results'}
                    {(statusFilter || globalFilter) && (
                      <span className="ml-1 text-blue-600">
                        (filtered)
                      </span>
                    )}
                  </span>
                </div>
              </div>
            </div>
            
            {/* Right: All Controls and Search */}
            <div className="flex flex-col gap-2 items-end min-w-0">
              {/* Row 1: All Controls on Right */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* Clear Filters Button (only show if filters are active) */}
                {statusFilter && (
                  <button
                    onClick={() => {
                      setStatusFilter('');
                      setPagination(p => ({ ...p, pageIndex: 0 }));
                    }}
                    className="inline-flex items-center px-2 py-1.5 border border-red-300 rounded-md shadow-sm text-xs text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-1 focus:ring-red-500 flex-shrink-0"
                  >
                    Clear Filters
                  </button>
                )}
                
                {/* View Mode Toggle */}
                <div className="flex-shrink-0 inline-flex rounded-md shadow-sm border border-gray-300 bg-white" role="group">
                  <button
                    onClick={() => handleViewModeChange('grid')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-blue-50 text-blue-700 border-r border-blue-200'
                        : 'text-gray-700 hover:bg-gray-50 border-r border-gray-300'
                    }`}
                    title="Grid View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                  </button>
                  <button
                    onClick={() => handleViewModeChange('table')}
                    className={`inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-r-md focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      viewMode === 'table'
                        ? 'bg-blue-50 text-blue-700'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    title="Table View"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                    </svg>
                  </button>
                </div>
                
                {/* Refresh Button */}
                <button
                  onClick={handleRefresh}
                  className="inline-flex items-center px-2 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
                  title="Refresh RFQs"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
                
                {/* Status Filter */}
                <div className="flex-shrink-0">
                  <select
                    value={statusFilter}
                    onChange={(e) => {
                      setStatusFilter(e.target.value);
                      setPagination(p => ({ ...p, pageIndex: 0 }));
                    }}
                    className={`text-sm px-2 py-1.5 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                      statusFilter ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white'
                    }`}
                  >
                    <option value="">All Status</option>
                    <option value="ready_to_send">Ready to Send</option>
                    <option value="0">Not Sent</option>
                    <option value="1">Open</option>
                    <option value="3">Closed</option>
                  </select>
                </div>
                
                {/* Action Buttons */}
                <button
                  onClick={() => navigate('/dashboard/requisitions')}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
                >
                  View Requisitions
                </button>
                
                <button
                  onClick={() => navigate('/dashboard/specifications')}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-md shadow-sm text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-indigo-500 flex-shrink-0"
                >
                  View Specifications
                </button>
                
                {can("Create RFQ") && (
                  <Link
                    to="/dashboard/rfqs/create"
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-sm text-white focus:outline-none focus:ring-1 flex-shrink-0"
                    style={{ backgroundColor: 'rgb(0, 135, 212)', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)', borderColor: 'rgb(0, 135, 212)' }}
                  >
                    <svg className="-ml-0.5 mr-1 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create RFQ
                  </Link>
                )}
              </div>
              
              {/* Row 2: Search Box (matches width of controls above) */}
              <div className="relative w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M8 4a4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                  </svg>
                </div>
                <DebouncedInput
                  value={globalFilter ?? ''}
                  onChange={value => {
                    setGlobalFilter(String(value));
                    setPagination(p => ({ ...p, pageIndex: 0 })); // Reset to first page when searching
                  }}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Search RFQs..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Reduce spacer */}
      <div className="mt-1"></div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 mt-1">
      
      {/* RFQs Grid */}
      {loading ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="flex justify-center items-center space-x-2">
            <svg className="animate-spin h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span>Loading RFQs...</span>
          </div>
        </div>
      ) : rfqs.length === 0 ? (
        <div className="bg-white rounded-lg p-4 shadow-sm flex items-center justify-center py-12">
          <div className="text-center">
            <div className="mx-auto h-24 w-24 text-gray-400 mb-4">
              <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No RFQs found</h3>
            <p className="text-gray-500 mb-6">
              {globalFilter ? 'Try adjusting your search terms.' : `No ${rfqType.toLowerCase()} RFQs have been created yet.`}
            </p>
            {can("Create RFQ") && !globalFilter && (
              <Link
                to="/dashboard/rfqs/create"
                className="inline-flex items-center px-6 py-3 text-white font-medium rounded-xl shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                style={{ 
                  backgroundColor: 'rgb(0, 135, 212)', 
                  backgroundImage: 'linear-gradient(to right, rgb(0, 135, 212), rgb(0, 115, 192))'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundImage = 'linear-gradient(to right, rgb(0, 115, 192), rgb(0, 105, 182))';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundImage = 'linear-gradient(to right, rgb(0, 135, 212), rgb(0, 115, 192))';
                }}
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create First RFQ
              </Link>
            )}
          </div>
        </div>
      ) : (
        <>
          {viewMode === 'grid' ? (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 mb-6">
              {table.getRowModel().rows.map((row, index) => {
                const rfq = row.original;
         
                // Handle quotes - it could be an array of quote objects or a count
                const quoteCount = Array.isArray(rfq.quotes) ? rfq.quotes.length : (typeof rfq.quotes === 'number' ? rfq.quotes : 0);
                const closingDate = safeParseDate(rfq.procurement_closing);
                const isExpired = closingDate && closingDate < new Date();
                const isClosed = rfq.procurement_closed;
                
                let statusText = 'Draft';
                let statusClass = 'bg-gray-100 text-gray-800';
                
                // Check procurement_state (1 = sent) or procurement_status (1 = active/sent)
                const isSent = (rfq.procurement_state == 1) || (rfq.procurement_status == 1);
                
                if (isClosed) {
                  statusText = 'Closed';
                  statusClass = 'bg-red-100 text-red-800';
                } else if (isExpired && isSent) {
                  statusText = 'Expired';
                  statusClass = 'bg-orange-100 text-orange-800';
                } else if (isSent) {
                  statusText = 'Open';
                  statusClass = 'bg-green-100 text-green-800';
                }
                
                return (
                  <div key={rfq.procurement_id} className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 hover:shadow hover:border-blue-200 transition-all duration-200 group flex flex-col h-full">
                    {/* Header with RFQ Reference and Status */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className="text-xs font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                          {String(rfq.procurement_ref || 'N/A')}
                        </span>
                        <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${statusClass}`}>
                          {statusText}
                        </span>
                      </div>
                    </div>

                    {/* Title */}
                    <Link
                      to={`/dashboard/rfqs/${rfq.procurement_id}`}
                      className="block"
                    >
                      <h3 className="font-semibold text-sm text-gray-900 hover:text-blue-600 transition-colors duration-200 mb-2 line-clamp-2 min-h-[2rem]">
                        {String(rfq.procurement_title || 'Untitled RFQ')}
                      </h3>
                    </Link>

                    {/* Flexible content area */}
                    <div className="flex-1 mb-2">
                      {/* Specification Link */}
                      {rfq.specification_title && (
                        <div className="mb-1.5 p-1.5 bg-purple-50 border border-purple-200 rounded text-xs">
                          <div className="flex items-center">
                            <svg className="w-2.5 h-2.5 text-purple-600 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            <Link
                              to={`/dashboard/specifications/${rfq.specification_id}`}
                              className="text-xs font-semibold text-purple-800 hover:underline truncate"
                            >
                              {String(rfq.specification_title || 'Specification')}
                            </Link>
                          </div>
                        </div>
                      )}

                      {/* Compact Metadata */}
                      <div className="space-y-0.5 text-xs text-gray-600">
                        <div className="flex items-center">
                          <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="truncate font-medium">{String(rfq.user_full_name || 'Unknown')}</span>
                        </div>
                        
                        <div className="flex items-center">
                          <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-2-5h6m-6 0V9m0 0h6m-6 0V5a2 2 0 012-2h4a2 2 0 012 2v4" />
                          </svg>
                          <span className="truncate">{String(rfq.department_name || 'General')}</span>
                        </div>

                        <div className="flex items-center">
                          <svg className="w-2.5 h-2.5 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span className={isExpired ? 'text-red-600 font-semibold' : ''}>
                            {closingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
                          </span>
                          <span className={`ml-auto inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                            quoteCount > 0
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {quoteCount} Quote{quoteCount !== 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-1 pt-2 border-t border-gray-100 mt-auto">
                      <Link
                        to={`/dashboard/rfqs/${rfq.procurement_id}`}
                        className="flex-1 inline-flex items-center justify-center px-2 py-1.5 text-white text-xs font-medium rounded-md shadow-sm hover:opacity-90 transition-all duration-200"
                        style={{ backgroundColor: 'rgb(0, 135, 212)', boxShadow: '0 1px 2px rgba(0, 135, 212, 0.3)' }}
                      >
                        <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                          <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                        </svg>
                        View
                      </Link>
                      
                      {can("Update RFQ") && rfq.procurement_status === 0 && (
                        <Link
                          to={`/dashboard/rfqs/${rfq.procurement_id}/edit`}
                          className="inline-flex items-center justify-center px-2 py-1.5 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded-md shadow-sm transition-colors duration-200"
                          title="Edit"
                        >
                          <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                          </svg>
                          Edit
                        </Link>
                      )}
                      
                      {can("Delete RFQ") && rfq.procurement_status === 0 && (
                        <button
                          type="button"
                          className="inline-flex items-center justify-center p-1.5 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                          onClick={() => handleDelete(rfq.procurement_id, rfq.procurement_title)}
                          title="Delete"
                        >
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd"></path>
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th 
                        scope="col" 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => {
                          const newSorting = sorting.length > 0 && sorting[0].id === 'procurement_id'
                            ? [{ id: 'procurement_id', desc: !sorting[0].desc }]
                            : [{ id: 'procurement_id', desc: false }];
                          setSorting(newSorting);
                        }}
                      >
                        <div className="flex items-center gap-1">
                          RFQ #
                          {sorting.length > 0 && sorting[0].id === 'procurement_id' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              {sorting[0].desc ? (
                                <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              ) : (
                                <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                      <th 
                        scope="col" 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => {
                          const newSorting = sorting.length > 0 && sorting[0].id === 'procurement_title'
                            ? [{ id: 'procurement_title', desc: !sorting[0].desc }]
                            : [{ id: 'procurement_title', desc: false }];
                          setSorting(newSorting);
                        }}
                      >
                        <div className="flex items-center gap-1">
                          Title
                          {sorting.length > 0 && sorting[0].id === 'procurement_title' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              {sorting[0].desc ? (
                                <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              ) : (
                                <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                      <th 
                        scope="col" 
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors select-none"
                        onClick={() => {
                          const newSorting = sorting.length > 0 && sorting[0].id === 'procurement_closing'
                            ? [{ id: 'procurement_closing', desc: !sorting[0].desc }]
                            : [{ id: 'procurement_closing', desc: true }];
                          setSorting(newSorting);
                        }}
                      >
                        <div className="flex items-center gap-1">
                          Closing Date
                          {sorting.length > 0 && sorting[0].id === 'procurement_closing' && (
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              {sorting[0].desc ? (
                                <path fillRule="evenodd" d="M14.707 12.293a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l2.293-2.293a1 1 0 011.414 0z" clipRule="evenodd" />
                              ) : (
                                <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                              )}
                            </svg>
                          )}
                        </div>
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Quotes
                      </th>
                      <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {table.getRowModel().rows.map(row => {
                      const rfq = row.original;
                      const quoteCount = Array.isArray(rfq.quotes) ? rfq.quotes.length : (typeof rfq.quotes === 'number' ? rfq.quotes : 0);
                      const closingDate = safeParseDate(rfq.procurement_closing);
                      const isExpired = closingDate && closingDate < new Date();
                      const isClosed = rfq.procurement_closed;
                      
                      let statusText = 'Draft';
                      let statusColor = 'gray';
                      
                      // Check procurement_state (1 = sent) or procurement_status (1 = active/sent)
                      const isSent = (rfq.procurement_state == 1) || (rfq.procurement_status == 1);
                      
                      if (isSent) {
                        if (isClosed) {
                          statusText = 'Closed';
                          statusColor = 'red';
                        } else if (isExpired) {
                          statusText = 'Expired';
                          statusColor = 'orange';
                        } else {
                          statusText = 'Open';
                          statusColor = 'green';
                        }
                      }
                      
                      return (
                        <tr key={rfq.procurement_id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="text-sm font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                              RFQ-{rfq.procurement_id}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/dashboard/rfqs/${rfq.procurement_id}`}
                              className="text-sm font-medium text-gray-900 hover:text-blue-600 line-clamp-2"
                            >
                              {rfq.procurement_title}
                            </Link>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-700">
                              {closingDate ? closingDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                            </div>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              statusColor === 'green' ? 'bg-green-100 text-green-800' :
                              statusColor === 'red' ? 'bg-red-100 text-red-800' :
                              statusColor === 'orange' ? 'bg-orange-100 text-orange-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {statusText}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                              quoteCount > 0 ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                            }`}>
                              {quoteCount} Quote{quoteCount !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end gap-1">
                              <Link
                                to={`/dashboard/rfqs/${rfq.procurement_id}`}
                                className="inline-flex items-center px-2 py-1 text-white text-xs font-medium rounded hover:opacity-90 transition-all"
                                style={{ backgroundColor: '#0087D4' }}
                                title="View"
                              >
                                <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z"></path>
                                  <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd"></path>
                                </svg>
                                View
                              </Link>
                              
                              {can("Edit RFQ") && (
                                <Link
                                  to={`/dashboard/rfqs/${rfq.procurement_id}/edit`}
                                  className="inline-flex items-center px-2 py-1 border border-gray-300 bg-white hover:bg-gray-50 text-gray-700 text-xs font-medium rounded transition-colors"
                                  title="Edit"
                                >
                                  <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                                  </svg>
                                  Edit
                                </Link>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

            {/* Pagination */}
            <div className="bg-white rounded-lg p-4 shadow-sm border border-gray-100 mt-4">
              <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Items per page:</span>
                  <select
                    value={table.getState().pagination.pageSize}
                    onChange={e => {
                      table.setPageSize(Number(e.target.value));
                    }}
                    className="block w-20 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  >
                    {[10, 20, 30, 40, 50].map(pageSize => (
                      <option key={pageSize} value={pageSize}>
                        {pageSize}
                      </option>
                    ))}
                  </select>
                  <span className="text-sm text-gray-600">entries per page</span>
                </div>

                <span className="flex items-center gap-1 text-sm text-gray-600">
                  Showing {Math.min(rowCount, pagination.pageIndex * pagination.pageSize + 1)} to {Math.min(rowCount, (pagination.pageIndex + 1) * pagination.pageSize)} of {rowCount} entries
                </span>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => table.firstPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  <span className="flex items-center gap-1 text-sm text-gray-600 px-4">
                    Page{' '}
                    <strong>
                      {table.getState().pagination.pageIndex + 1} of{' '}
                      {table.getPageCount()}
                    </strong>
                  </span>
                  
                  <button
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  <button
                    onClick={() => table.lastPage()}
                    disabled={!table.getCanNextPage()}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
        </>
        )}
      </div>
    </div>
  );
}

export default RFQsList;
